import type { StagedCreateImage } from './detail/catalogProductDetailHelpers';
import { API_BASE } from '../../lib/apiBase';

/** API field → étape assistant création (`wizardSteps`). */
export const CREATE_WIZARD_FIELD_STEP: Record<string, string> = {
  name: 'Identité',
  slug: 'Identité',
  productTypeCode: 'Classification',
  categoryId: 'Classification',
  markeId: 'Marque',
  variantAxes: 'Variantes',
  description: 'Marque',
};

export function pickFirstWizardErrorJump(
  errors: Record<string, string>,
  wizardSteps: readonly string[],
): { stepIndex: number } | null {
  const keys = Object.keys(errors).filter((k) => k !== '_form');
  if (keys.length === 0) return null;

  const priority = ['name', 'slug', 'productTypeCode', 'categoryId', 'markeId', 'variantAxes', 'description'];
  const ordered = [...priority.filter((k) => keys.includes(k)), ...keys.filter((k) => !priority.includes(k))];

  for (const key of ordered) {
    let stepName = CREATE_WIZARD_FIELD_STEP[key];
    if (!stepName) {
      if (/slug|^name$/i.test(key) || key.endsWith('.name')) stepName = 'Identité';
      else if (/categor|marke|productType|typeCode/i.test(key)) stepName = 'Classification';
      else if (/variant|matrix|option/i.test(key)) stepName = 'Variantes';
      else if (/desc|channel|visible|web|show/i.test(key)) stepName = 'Détails';
      else if (/media|image|thumbnail/i.test(key)) stepName = 'Images';
    }
    if (!stepName) continue;
    const stepIndex = wizardSteps.indexOf(stepName);
    if (stepIndex >= 0) return { stepIndex };
  }
  return { stepIndex: 0 };
}

export function isAllowedImageFile(file: File): boolean {
  if (file.type?.startsWith('image/')) return true;
  // Some browsers/devices provide empty mime; fall back to filename extension.
  const name = file.name?.toLowerCase() ?? '';
  return /\.(jpe?g|png|webp|gif)$/.test(name);
}

export function newStagedCreateImageId(): string {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* ponytail: non-secure context */
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** ponytail: browser fetch may fail on CORS; caller ignores null */
export async function fetchStagedCreateImageFromUrl(
  url: string,
  isAllowed: (file: File) => boolean = isAllowedImageFile,
): Promise<StagedCreateImage | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    const ext = blob.type.split('/')[1]?.split('+')[0] || 'jpg';
    const file = new File([blob], `obf.${ext}`, { type: blob.type });
    if (!isAllowed(file)) return null;
    return {
      id: newStagedCreateImageId(),
      source: 'file',
      file,
      previewUrl: URL.createObjectURL(file),
    };
  } catch {
    return null;
  }
}

/** Fetch OBF/external image via API proxy (avoids browser CORS). */
export async function fetchCatalogProxyImageFile(
  imageUrl: string,
  token: string,
  isAllowed: (file: File) => boolean = isAllowedImageFile,
): Promise<File | null> {
  try {
    const proxy = `${API_BASE}/catalog/admin/external-catalog/image?url=${encodeURIComponent(imageUrl)}`;
    const res = await fetch(proxy, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith('image/')) return null;
    const ext = blob.type.split('/')[1]?.split('+')[0] || 'jpg';
    const file = new File([blob], `brand-logo-${Date.now()}.${ext}`, { type: blob.type });
    if (!isAllowed(file)) return null;
    return file;
  } catch {
    return null;
  }
}

/** Fetch OBF/external image via API proxy (avoids browser CORS). */
export async function fetchStagedCreateImageFromCatalogProxy(
  imageUrl: string,
  token: string,
  isAllowed: (file: File) => boolean = isAllowedImageFile,
): Promise<StagedCreateImage | null> {
  const file = await fetchCatalogProxyImageFile(imageUrl, token, isAllowed);
  if (!file) return null;
  return {
    id: newStagedCreateImageId(),
    source: 'file',
    file,
    previewUrl: URL.createObjectURL(file),
  };
}
